<script setup lang="ts">
import type { NuxtError } from '#app'

const Error404 = defineAsyncComponent(
  () => import('./components/Error/404.vue'),
)
const Error500 = defineAsyncComponent(
  () => import('./components/Error/500.vue'),
)
const ErrorContractBadAccount = defineAsyncComponent(
  () => import('./components/Error/ContractBadAccount.vue'),
)
const ErrorClientContractNotSignedByOwner = defineAsyncComponent(
  () => import('./components/Error/ClientContractNotSignedByOwner.vue'),
)
const ErrorWhiteBrand = defineAsyncComponent(
  () => import('./components/Error/WhiteBrand.vue'),
)

const { setRoute } = useHeaderNavigation()
const { height } = useHeader()
const { trackPageViewErrorPage } = useTracking()
const { languageCurrencyOpen, signInChoiceOpen, searchMobileOpen } = useHeader()
const {
  public: { environment },
} = useRuntimeConfig()

const { error } = defineProps<{
  error?: NuxtError & { url?: string }
}>()

// handle header theme
setRoute('destinations___fr')
trackPageViewErrorPage()

const components = reactive({
  404: {
    componentName: shallowRef(Error404),
  },
  500: {
    componentName: shallowRef(Error500),
  },
  contractBadAccount: {
    componentName: shallowRef(ErrorContractBadAccount),
  },
  contractNotSignedByOwner: {
    componentName: shallowRef(ErrorClientContractNotSignedByOwner),
  },
  whitebrand: {
    componentName: shallowRef(ErrorWhiteBrand),
  },
})

const cssVar = computed(() => ({
  '--header-height': `${height.value}px`,
}))

const data = computed(() =>
  error?.data
    ? typeof error.data === 'string'
      ? JSON.parse(error.data)
      : error.data
    : {},
)

const type = computed<keyof typeof components>(() => {
  if (error?.statusCode === 404) {
    if (data.value.page === 'whitebrand') {
      return 'whitebrand'
    }

    return data?.value?.page || 404
  }

  return 500
})

const isProduction = computed(() => ['production'].includes(environment))
</script>

<template>
  <div :style="cssVar" class="flex min-h-screen flex-col">
    <template v-if="type !== 'whitebrand'">
      <LazyVisibleTheHeader hydrate-on-visible />
      <LazyIdleTheHeaderMenu hydrate-on-idle />
    </template>

    <div>
      <component
        :is="components[type].componentName"
        :error="error"
        :data="data"
      />

      <div
        v-if="error && !isProduction"
        class="mx-auto flex w-[1280px] flex-col gap-4 py-10"
      >
        <template v-if="error.url">
          <strong>Url</strong>
          <p>{{ error.url }}</p>
        </template>
        <template v-if="error.statusCode">
          <strong>StatusCode</strong>
          <p>{{ error.statusCode }}</p>
        </template>
        <template v-if="error.statusMessage">
          <strong>StatusMessage</strong>
          <p class="w-[1280px] bg-gray-100 p-4">{{ error.statusMessage }}</p>
        </template>
        <template v-if="error.message">
          <strong>Message</strong>
          <p class="w-[1280px] bg-gray-100 p-4">{{ error.message }}</p>
        </template>
        <template v-if="error.data">
          <strong>Custom data</strong>
          <pre class="w-[1280px] bg-gray-100 p-4">{{ error.data }}</pre>
        </template>
        <template v-if="error.stack">
          <strong>Stack</strong>
          <p class="w-[1280px] bg-gray-100 p-4">{{ error.stack }}</p>
        </template>
      </div>
    </div>

    <LazyVisibleTheFooter v-if="type !== 'whitebrand'" hydrate-on-visible />

    <ClientOnly>
      <LazyThePopinLanguageAndCurrency v-if="languageCurrencyOpen" />
      <LazyThePopinSearchMobileLocation v-if="searchMobileOpen" />
      <LazyThePopinSignInChoice v-if="signInChoiceOpen" />
    </ClientOnly>
  </div>
</template>
